/**
 * AddDocumentLinkForm — the farmer links this document to a twin object by
 * hand (FR-017.7).
 *
 * `generic`, `validity` and `ownership` are always offered. `price` and
 * `quantity` need a line anchor (`libs/twin_links` TYPE_RULES), so they join
 * only when the document has a row that can be anchored (FR-017 #5); choosing
 * one opens the line picker, and picking a row prefills the editable values.
 *
 * `price` targets entities only. The object-type list needs no narrowing for
 * it: it is built from the lens registry's concrete types, and the event
 * family has no lens (which is also why an event target renders as plain
 * text). Should a lens ever show one, the server's `target_kind_not_allowed`
 * is named by `linkErrors`.
 */

import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { linkWriteErrorMessage } from '../../components/twinLinks/linkErrors';
import {
  EMPTY_LINE_DRAFT,
  LINE_LINK_TYPES,
  LinkTypeFields,
  OFFERED_LINK_TYPES,
  isLineLinkType,
  linkPayload,
  linkTypeComplete,
  type LineDraft,
} from '../../components/twinLinks/LinkTypeFields';
import { isAnchorableRow, prefillFromRow, todayIso, type LineRow } from '../../components/twinLinks/lineRows';
import { entityTypeLabel } from '../../components/twinLinks/linkTargetDisplay';
import { twinService } from '../../services/twinService';
import LinePicker from './LinePicker';

export interface LinkTargetType {
  entityType: string;
  label: string;
}

export interface EntitySuggestion {
  entity_id: string;
  name: string;
}

interface AddDocumentLinkFormProps {
  documentId: string;
  targetTypes: readonly LinkTargetType[];
  searchEntities: (entityType: string, q: string) => Promise<EntitySuggestion[]>;
  onCreated: () => void;
  onCancel: () => void;
  /** The document's extracted rows (`documentRows`); none hides price/quantity. */
  lines?: readonly LineRow[];
  /** The document's date, the default observation date of a line link. */
  documentDate?: string | null;
}

const NO_LINES: readonly LineRow[] = [];

/** Fewer characters than this match half the farm and say nothing. */
const MIN_QUERY = 2;
/** The search reads every attribute of the type, so it waits for a pause in typing. */
const SEARCH_DEBOUNCE_MS = 250;

export const AddDocumentLinkForm: React.FC<AddDocumentLinkFormProps> = ({
  documentId,
  targetTypes,
  searchEntities,
  onCreated,
  onCancel,
  lines = NO_LINES,
  documentDate = null,
}) => {
  const { t } = useTranslation();
  const [entityType, setEntityType] = React.useState(targetTypes[0]?.entityType ?? '');
  const [query, setQuery] = React.useState('');
  const [target, setTarget] = React.useState<EntitySuggestion | null>(null);
  const [linkType, setLinkType] = React.useState('generic');
  const [validUntil, setValidUntil] = React.useState('');
  const [line, setLine] = React.useState<LineDraft>(EMPTY_LINE_DRAFT);

  const offered = React.useMemo<readonly string[]>(
    () => (lines.some(isAnchorableRow) ? [...OFFERED_LINK_TYPES, ...LINE_LINK_TYPES] : OFFERED_LINK_TYPES),
    [lines],
  );
  const anchored = isLineLinkType(linkType);

  const pickLine = (index: number) =>
    // The farmer's direction survives a re-pick; the values are the new row's.
    setLine((prev) => ({
      ...prefillFromRow(lines[index], documentDate, todayIso()),
      anchorRef: String(index),
      direction: prev.direction,
    }));

  const trimmed = query.trim();
  const [searched, setSearched] = React.useState(trimmed);
  React.useEffect(() => {
    // A cleared box (a type change clears it) takes effect at once: a stale term
    // must never search the NEW type during the pause.
    if (trimmed.length < MIN_QUERY) {
      setSearched(trimmed);
      return undefined;
    }
    const timer = setTimeout(() => setSearched(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);
  const suggestions = useQuery({
    queryKey: ['twin', 'link-target-suggest', entityType, searched],
    queryFn: () => searchEntities(entityType, searched),
    enabled: Boolean(entityType) && searched.length >= MIN_QUERY && target === null,
  });

  const create = useMutation({
    mutationFn: () =>
      twinService.createLink({
        document_id: documentId,
        target_type: entityType,
        target_id: target!.entity_id,
        link_type: linkType,
        ...(anchored ? { anchor_kind: 'line' as const, anchor_ref: line.anchorRef! } : {}),
        payload: linkPayload(linkType, validUntil, line),
      }),
    onSuccess: onCreated,
  });

  const canSubmit = target !== null && linkTypeComplete(linkType, validUntil, line) && !create.isPending;

  return (
    <form
      className="document-twin-links__add"
      aria-label={t('twinLinks.form.label')}
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) create.mutate();
      }}
    >
      <label>
        {t('twinLinks.form.objectType')}
        <select
          value={entityType}
          onChange={(e) => {
            // A term typed for one type is noise against another; keeping it
            // re-runs the old search and offers objects of the wrong kind.
            setEntityType(e.target.value);
            setTarget(null);
            setQuery('');
            setSearched('');
          }}
        >
          {targetTypes.map((type) => (
            <option key={type.entityType} value={type.entityType}>
              {entityTypeLabel(type.entityType, type.label)}
            </option>
          ))}
        </select>
      </label>

      {target ? (
        <p className="document-twin-links__picked">
          {target.name || t('twinLinks.form.unnamed')}{' '}
          <button type="button" className="document-twin-links__text-btn" onClick={() => setTarget(null)}>
            {t('twinLinks.form.change')}
          </button>
        </p>
      ) : (
        <>
          <label>
            {t('twinLinks.form.search')}
            <input
              type="search"
              value={query}
              placeholder={t('twinLinks.form.searchPlaceholder')}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          {suggestions.data && suggestions.data.length === 0 && <p>{t('twinLinks.form.noMatch')}</p>}
          {suggestions.data && suggestions.data.length > 0 && (
            <ul role="listbox" aria-label={t('twinLinks.form.matches')}>
              {suggestions.data.map((s) => (
                <li key={s.entity_id} role="option" aria-selected={false}>
                  <button type="button" className="document-twin-links__suggestion" onClick={() => setTarget(s)}>
                    {s.name || t('twinLinks.form.unnamed')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <LinkTypeFields
        linkType={linkType}
        onLinkType={setLinkType}
        validUntil={validUntil}
        onValidUntil={setValidUntil}
        offered={offered}
        line={line}
        onLine={setLine}
      >
        <LinePicker rows={lines} selected={line.anchorRef} onSelect={pickLine} />
      </LinkTypeFields>

      <div className="document-twin-links__form-actions">
        <button type="button" className="document-twin-links__text-btn" onClick={onCancel}>
          {t('twinLinks.form.cancel')}
        </button>
        <button
          type="submit"
          className="document-twin-links__text-btn document-twin-links__text-btn--primary"
          disabled={!canSubmit}
        >
          {t('twinLinks.form.submit')}
        </button>
      </div>

      {create.isError && (
        <p role="alert" className="document-twin-links__error">
          {linkWriteErrorMessage(create.error)}
        </p>
      )}
    </form>
  );
};

export default AddDocumentLinkForm;
